import React, { useEffect, useState, useRef } from 'react';
import { ComponentLoader } from '../common/Loader';
import { Form, Spinner } from 'react-bootstrap';
import Select from 'react-select'; // Import react-select
import { fetchStats, fetchCostStats } from './api';
import StatsSkeleton from './StatsSkeleton';
import { calculateCost, INR_RATE } from './constants';

const DashboardStats = ({ filters }) => {
    const [stats, setStats] = useState({ total: 0, completed: 0, failed: 0, in_progress: 0 });
    const [loading, setLoading] = useState(true);

    // Cost Stats State
    const [costStats, setCostStats] = useState(null);
    const [costLoading, setCostLoading] = useState(true);

    // Enhanced Filters State
    const [timeFilterType, setTimeFilterType] = useState('current_month'); // 'current_month', 'select_month', 'yearly', 'all_time'
    const [costMonth, setCostMonth] = useState(new Date().getMonth() + 1);
    const [costYear, setCostYear] = useState(new Date().getFullYear());
    const [selectedModel, setSelectedModel] = useState({ value: 'All', label: 'All Models' });
    const [availableModels, setAvailableModels] = useState([]); // List for dropdown
    const lastFiltersRef = useRef(null);
    const lastCostFiltersRef = useRef(null);

    useEffect(() => {
        const cacheKey = `AI_INSIGHTS_OVERVIEW_CACHE_${JSON.stringify(filters)}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        let cacheExists = false;
        
        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') {
                return;
            }

            if (data && typeof data === 'object') {
                const cacheTimestamp = data.cacheTimestamp || 0;
                const threeMinutes = 3 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > threeMinutes;

                if (!isExpired) {
                    setStats(data.stats);
                    setLoading(false);
                    lastFiltersRef.current = JSON.stringify(filters);
                    return;
                } else {
                    setStats(data.stats);
                    setLoading(false);
                    cacheExists = true;
                }
            }
        }

        const currentFiltersStr = JSON.stringify(filters);
        if (lastFiltersRef.current === currentFiltersStr && !cacheExists) return;
        lastFiltersRef.current = currentFiltersStr;

        const loadStats = async () => {
            if (!cacheExists) {
                setLoading(true);
            }
           
            sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));

            const res = await fetchStats(filters);
            if (res.success && res.data && res.data.stats) {
                setStats(res.data.stats);
                
                // Save to Cache
                const cacheData = {
                    stats: res.data.stats,
                    cacheTimestamp: Date.now()
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
               
            } else {
                sessionStorage.removeItem(cacheKey);
            }
            setLoading(false);
        };
        loadStats();
    }, [filters]);

    // Load Cost Stats
    useEffect(() => {
        const cacheKey = `AI_INSIGHTS_COST_CACHE_${timeFilterType}_${costMonth}_${costYear}_${selectedModel.value}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        let cacheExists = false;

        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') {
               
                return;
            }

            if (data && typeof data === 'object') {
                const cacheTimestamp = data.cacheTimestamp || 0;
                const threeMinutes = 3 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > threeMinutes;
                if (!isExpired) {
                   
                    setCostStats(data.costStats);
                    setAvailableModels(data.availableModels);
                    setCostLoading(false);
                    lastCostFiltersRef.current = JSON.stringify([timeFilterType, costMonth, costYear, selectedModel]);
                    return;
                } else {
                   
                    setCostStats(data.costStats);
                    setAvailableModels(data.availableModels);
                    setCostLoading(false);
                    cacheExists = true;
                } 
            }
        }

        const currentCostDeps = JSON.stringify([timeFilterType, costMonth, costYear, selectedModel]);
        if (lastCostFiltersRef.current === currentCostDeps && !cacheExists) return;
        lastCostFiltersRef.current = currentCostDeps;

        const loadCostStats = async () => {
            if (!cacheExists) {
                setCostLoading(true);
            }
            sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));

            let startDate = null;
            let endDate = null;

            // Date Logic based on Filter Type
            if (timeFilterType === 'current_month') {
                const now = new Date();
                startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
            } else if (timeFilterType === 'select_month') {
                startDate = new Date(costYear, costMonth - 1, 1).toISOString().split('T')[0];
                endDate = new Date(costYear, costMonth, 0).toISOString().split('T')[0];
            } else if (timeFilterType === 'yearly') {
                startDate = new Date(costYear, 0, 1).toISOString().split('T')[0];
                endDate = new Date(costYear, 11, 31).toISOString().split('T')[0];
            }
            // 'all_time' leaves startDate and endDate as null

            const costFilters = {
                startDate,
                endDate,
                model: selectedModel.value
            };

            const res = await fetchCostStats(costFilters);

            if (res.success && res.data) {
                const { usageByModel, availableModels: backendModels } = res.data;

                // Process Usage Data
                let totalUSD = 0;
                const processed = (usageByModel || []).map(item => {
                    const { totalCost } = calculateCost(item.model, item.promptTokens, item.candidatesTokens);
                    totalUSD += totalCost;
                    return { ...item, costUSD: totalCost, costINR: totalCost * INR_RATE };
                });

                const costStatsData = {
                    usageByModel: processed,
                    totalUSD,
                    totalINR: totalUSD * INR_RATE
                };

                setCostStats(costStatsData);

                // Update Available Models for Dropdown
                let modelOptions = availableModels;
                if (backendModels) {
                    modelOptions = [
                        { value: 'All', label: 'All Models' },
                        ...backendModels.map(m => ({ value: m, label: m }))
                    ];
                    setAvailableModels(modelOptions);
                }

                // Save to Cache
                const cacheData = {
                    costStats: costStatsData,
                    availableModels: modelOptions,
                    cacheTimestamp: Date.now()
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } else {
                sessionStorage.removeItem(cacheKey);
            }
            setCostLoading(false);
        };
        loadCostStats();
    }, [timeFilterType, costMonth, costYear, selectedModel]); // Trigger on any filter change

    const formatCurrency = (val, currency = 'USD') => {
        if (currency === 'INR') return `₹${val.toFixed(2)}`;
        return `$${val.toFixed(4)}`;
    };

    if (loading) {
        return <StatsSkeleton />;
    }

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Select Styles (to match AuditFilters)
    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '32px',
            fontSize: '13px',
            borderColor: '#e2e8f0',
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        }),
        menu: (base) => ({
            ...base,
            fontSize: '13px',
            zIndex: 9999
        })
    };

    return (
        <div>
            {/* Status Grid */}
            <div className="aii-stats-grid">
                <div className="aii-stat-card aii-stat-total">
                    <div className="aii-stat-icon">
                        <i className="fa fa-layer-group"></i>
                    </div>
                    <div>
                        {loading ? <h3 className="aii-stat-value"><ComponentLoader /></h3> : <h3 className="aii-stat-value">{stats.total}</h3>}
                        <p className="aii-stat-label">Total Requests</p>
                    </div>
                </div>

                <div className="aii-stat-card aii-stat-success">
                    <div className="aii-stat-icon">
                        <i className="fa fa-check-circle"></i>
                    </div>
                    <div>
                        {loading ? <h3 className="aii-stat-value"><ComponentLoader /></h3> : <h3 className="aii-stat-value">{stats.completed}</h3>}
                        <p className="aii-stat-label">Completed</p>
                    </div>
                </div>

                <div className="aii-stat-card aii-stat-fail">
                    <div className="aii-stat-icon">
                        <i className="fa fa-times-circle"></i>
                    </div>
                    <div>
                        {loading ? <h3 className="aii-stat-value"><ComponentLoader /></h3> : <h3 className="aii-stat-value">{stats.failed}</h3>}
                        <p className="aii-stat-label">Failed</p>
                    </div>
                </div>

                <div className="aii-stat-card aii-stat-progress">
                    <div>
                        {loading ? <h3 className="aii-stat-value"><ComponentLoader /></h3> : <h3 className="aii-stat-value">{stats.in_progress + (stats.pending || 0)}</h3>}
                        <p className="aii-stat-label">In Progress</p>
                    </div>
                </div>
            </div>

            {/* Cost Analysis Section */}
            <div style={{ marginTop: '32px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                            width: '24px', height: '24px', backgroundColor: '#2563eb', borderRadius: '6px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px'
                        }}>💰</div>
                        <h6 style={{ margin: 0, fontWeight: '700', color: '#334155', textTransform: 'uppercase' }}>
                            Cost Analysis ({timeFilterType.replace('_', ' ')})
                        </h6>
                    </div>

                    {/* Enhanced Filters */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

                        {/* 1. Time Filter Type */}
                        <Form.Select
                            size="sm"
                            style={{ width: '130px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                            value={timeFilterType}
                            onChange={(e) => setTimeFilterType(e.target.value)}
                        >
                            <option value="current_month">Current Month</option>
                            <option value="select_month">Select Month</option>
                            <option value="yearly">Yearly</option>
                            <option value="all_time">All Time</option>
                        </Form.Select>

                        {/* 2. Conditionals: Month/Year Dropdowns */}
                        {timeFilterType === 'select_month' && (
                            <Form.Select
                                size="sm"
                                style={{ width: '110px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                value={costMonth}
                                onChange={(e) => setCostMonth(parseInt(e.target.value))}
                            >
                                {months.map((m, i) => (
                                    <option key={i} value={i + 1}>{m}</option>
                                ))}
                            </Form.Select>
                        )}

                        {(timeFilterType === 'select_month' || timeFilterType === 'yearly') && (
                            <Form.Select
                                size="sm"
                                style={{ width: '80px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                value={costYear}
                                onChange={(e) => setCostYear(parseInt(e.target.value))}
                            >
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </Form.Select>
                        )}

                        {/* 3. Model Filter (React Select) */}
                        <div style={{ width: '200px' }}>
                            <Select
                                options={availableModels}
                                value={selectedModel}
                                onChange={setSelectedModel}
                                styles={selectStyles}
                                placeholder="Select Model..."
                                isSearchable
                            />
                        </div>
                    </div>
                </div>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '24px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                    {costLoading ? (
                        <div style={{
                            padding: '60px',
                            textAlign: 'center',
                            color: '#475569',
                            fontFamily: 'sans-serif',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <ComponentLoader color="#3b82f6" size="48px" padding="0 0 16px 0" />
                            <div style={{ fontSize: '18px', fontWeight: '500', letterSpacing: '0.5px' }}>
                                Calculating costs...
                            </div>
                            <div style={{ fontSize: '14px', color: '#94a3b8', marginTop: '8px' }}>
                                Please wait while we crunch the numbers 🔢
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                            {/* Total Cost Big Number */}
                            <div style={{ minWidth: '200px', borderRight: '1px solid #e2e8f0', paddingRight: '24px' }}>
                                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '600' }}>
                                    Total Spend
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', lineHeight: '1' }}>
                                    {formatCurrency(costStats?.totalUSD || 0)}
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: '#166534', marginTop: '4px' }}>
                                    {formatCurrency(costStats?.totalINR || 0, 'INR')}
                                </div>
                                <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
                                    {timeFilterType === 'all_time' ? 'All historical data' :
                                        timeFilterType === 'yearly' ? `Full Year ${costYear}` :
                                            timeFilterType === 'select_month' ? `${months[costMonth - 1]} ${costYear}` :
                                                'This Month so far'}
                                </div>
                            </div>

                            {/* Breakdown Table */}
                            <div style={{ flex: 1, overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #f1f5f9', textAlign: 'left' }}>
                                            <th style={{ padding: '8px', color: '#64748b' }}>Model</th>
                                            <th style={{ padding: '8px', color: '#64748b', textAlign: 'right' }}>Requests</th>
                                            <th style={{ padding: '8px', color: '#64748b', textAlign: 'right' }}>Total Tokens</th>
                                            <th style={{ padding: '8px', color: '#64748b', textAlign: 'right' }}>Cost (USD)</th>
                                            <th style={{ padding: '8px', color: '#64748b', textAlign: 'right' }}>Cost (INR)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {costStats?.usageByModel?.length > 0 ? (
                                            costStats.usageByModel.map((item, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid #f8fafc' }}>
                                                    <td style={{ padding: '8px', fontWeight: '600', color: '#334155' }}>
                                                        {item.model}
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: '#475569' }}>
                                                        {item.requestCount}
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: '#475569' }}>
                                                        {(item.totalTokens / 1000).toFixed(1)}k
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#334155' }}>
                                                        {formatCurrency(item.costUSD)}
                                                    </td>
                                                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#166534' }}>
                                                        {formatCurrency(item.costINR, 'INR')}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            // <tr>
                                            //     <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                            //         No usage data for this criteria
                                            //     </td>
                                            // </tr>
                                            <tr>
                                                <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                                    <div className="animate-bounce-in">
                                                        <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>
                                                            📊
                                                        </span>
                                                        No usage data for this criteria
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardStats;
